import { c as _c } from "react/compiler-runtime";
import { cn } from '../../utils/cn';
import { Skeleton } from '../_base/Skeleton';
const INNER_GRIDLINES = [20, 40, 60, 80];
const SKELETON_CURVE = 'M0 68 C 40 40, 80 30, 120 36 S 200 88, 260 70 S 340 20, 400 32 S 470 72, 520 56 S 600 10, 660 22 S 740 60, 800 46';
interface ChartSkeletonProps {
  /** Screen-reader text announced while the chart loads. */
  label?: string;
  hideXAxis?: boolean;
  /** Corner radius (px) of the plot area, to match the chart it stands in for. */
  plotRadius?: number;
  /**
   * Gap (px) above the plot and below it, x-axis row included, to line the plot up with the chart's
   * own margins. Defaults to the markets chart layout.
   */
  plotMargin?: {
    top: number;
    bottom: number;
  };
  /** Plot border width (px), to match the chart's plot border. */
  plotBorderWidth?: number;
  /** Gap (px) between an x-axis tick and its label, to match the chart's label offset. */
  xLabelGap?: number;
  className?: string;
}

/** Chart-shaped loading placeholder: gridlines, a pulsing curve and axis label stubs. Fills its parent. */
function ChartSkeleton(t0) {
  const $ = _c(28);
  const {
    label: t1,
    hideXAxis: t2,
    plotRadius: t3,
    plotMargin,
    plotBorderWidth: t4,
    xLabelGap: t5,
    className
  } = t0;
  const label = t1 === undefined ? "Loading chart\u2026" : t1;
  const hideXAxis = t2 === undefined ? false : t2;
  const plotRadius = t3 === undefined ? 5 : t3;
  const plotBorderWidth = t4 === undefined ? 0.5 : t4;
  const xLabelGap = t5 === undefined ? 6 : t5;
  let t6;
  if ($[0] !== className) {
    t6 = cn("flex h-full min-h-0 w-full shrink-0 flex-col", className);
    $[0] = className;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  let t7;
  if ($[2] !== label) {
    t7 = <span className="sr-only">{label}</span>;
    $[2] = label;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const t8 = !plotMargin && "mt-2 mb-1";
  let t9;
  if ($[4] !== t8) {
    t9 = cn("bg-muted dark:bg-accent border-border relative flex-1 border-solid", t8);
    $[4] = t8;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const t10 = `${plotRadius}px`;
  const t11 = `${plotBorderWidth}px`;
  let t12;
  if ($[6] !== hideXAxis || $[7] !== plotMargin) {
    t12 = plotMargin && {
      marginTop: `${plotMargin.top}px`,
      marginBottom: hideXAxis ? `${plotMargin.bottom}px` : 0
    };
    $[6] = hideXAxis;
    $[7] = plotMargin;
    $[8] = t12;
  } else {
    t12 = $[8];
  }
  let t13;
  if ($[9] !== t10 || $[10] !== t11 || $[11] !== t12) {
    t13 = {
      borderRadius: t10,
      borderWidth: t11,
      ...t12
    };
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = INNER_GRIDLINES.map(_temp);
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  let t15;
  let t16;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <svg className="text-border absolute inset-0 h-full w-full animate-pulse" viewBox="0 0 800 100" preserveAspectRatio="none" aria-hidden="true"><path d={SKELETON_CURVE} fill="none" stroke="currentColor" strokeWidth={3} vectorEffect="non-scaling-stroke" /></svg>;
    t16 = INNER_GRIDLINES.map(_temp2);
    $[14] = t15;
    $[15] = t16;
  } else {
    t15 = $[14];
    t16 = $[15];
  }
  let t17;
  if ($[16] !== t13 || $[17] !== t9) {
    t17 = <div className={t9} style={t13}>{t14}{t15}{t16}</div>;
    $[16] = t13;
    $[17] = t9;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== hideXAxis || $[20] !== plotMargin || $[21] !== xLabelGap) {
    t18 = !hideXAxis && <div className={cn("flex justify-between", !plotMargin && "h-7")} style={plotMargin && {
      height: `${plotMargin.bottom}px`
    }}>{[0, 1, 2, 3, 4, 5].map(index => <div key={index} className="flex flex-col items-center" style={{
        gap: `${xLabelGap}px`
      }}><div className="bg-border h-[5px] w-px" /><Skeleton className="h-2 w-9" /></div>)}</div>;
    $[19] = hideXAxis;
    $[20] = plotMargin;
    $[21] = xLabelGap;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== t17 || $[24] !== t18 || $[25] !== t6 || $[26] !== t7) {
    t19 = <div data-testid="chart-skeleton" aria-busy="true" className={t6}>{t7}{t17}{t18}</div>;
    $[23] = t17;
    $[24] = t18;
    $[25] = t6;
    $[26] = t7;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  return t19;
}
function _temp2(top_0) {
  return <Skeleton key={top_0} className="bg-border absolute right-2.5 h-2.5 w-9 -translate-y-1/2" style={{
    top: `${top_0}%`
  }} />;
}
function _temp(top) {
  return <div key={top} className="border-border/50 absolute inset-x-0 border-t" style={{
    top: `${top}%`
  }} />;
}
export { ChartSkeleton };
export type { ChartSkeletonProps };